<script setup lang="ts">
import { ProjectBaseFragment } from "@/graphql";
import Modal from "../../Reusables/Modal/Modal.vue";
import ManageMembersPanel from "./ManageMembersPanel.vue";

/**
 * The member list as a dialog, for the places that open it from a row or a card action — the project
 * list's kebab menu and the old project navigation.
 *
 * A shell and nothing else: every bit of behaviour lives in `ManageMembersPanel`, which the project
 * settings page renders inline instead, so the members are simply on the page there rather than one
 * click behind a button. Keeping the two shapes over one implementation is the point — the list, the
 * invite row and the role menu cannot drift apart between them.
 */
const props = defineProps<{
  project: ProjectBaseFragment;
}>();

const emits = defineEmits(["toggleInviteModal"]);

const toggleView = () => {
  emits("toggleInviteModal");
};
</script>

<template>
  <!-- Fixed width rather than a proportion: at w-3/4 the rows stretched to ~1080px on a 1440px
       screen, leaving ~700px of dead space between a member's name and their role, so associating
       the two meant crossing the whole dialog. A max-w-* utility can't be used to cap it from here
       because Modal.vue's own base classes already set max-w-[85vw] and, being two competing
       max-width utilities, the winner is decided by stylesheet order rather than by this class list
       (measured: 85vw won). Setting width sidesteps that, and the base max-w-[85vw] still keeps it
       inside narrow viewports. -->
  <Modal
    :heading="`Manage users in ${project.name}`"
    size="w-[672px] h-fit"
    @toggle-modal="toggleView">
    <ManageMembersPanel :project="props.project" />
  </Modal>
</template>
